import BottomSheet from '../bottom-sheet/BottomSheet';
import dayjs, { Dayjs } from 'dayjs';
import logger from '../../utils/logger';
import NoIconImage from '../../assets/placeholder-image-small.png';
import store, { RootState, useAppDispatch, useAppSelector } from '../../redux/store';
import utc from 'dayjs/plugin/utc';
import { activeMenu, dispatchToggleMenuEvent } from '../../utils/menu';
import { AnalyticsEventAttribute } from '../../constants/AnalyticsEventAttribute';
import { CartItem, CartModalConfigType, CartOperationType, DeliveryType } from '../../types/cart';
import { checkInvalidStartDate } from '../../utils/cart';
import { closeCartModal, setCartItem, toggleCartHeaderDropdown } from '../../redux/slice/cartSlice';
import { currentLocationStatus } from '../../constants/error';
import { CurrentLocationStatus } from '../../types/location';
import { DataLayerEventName } from '../../types/data-layer';
import { FormattedMessage, useIntl } from 'react-intl';
import { getEquipmentClassListSearchResults } from '../../apis/equipment-search';
import { getPriceCatalogKey, isPriceInitialized } from '../../utils/equipment';
import { isAuthenticated } from '../../utils/authUtils';
import { LocationHelperTexts } from '../../types/autocomplete';
import { Menu } from '../../types/menu';
import { messageContents } from '../../constants/message';
import { openMessageModal } from '../../utils/message';
import { pushEventToDataLayer } from '../../utils/dataLayerUtils';
import { showContactForPrice } from '@app/utils/price';
import { Subject } from 'rxjs';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useFallbackImageManager } from '@app/hooks/useImageManager';
import {
  ConfigContainer,
  Product,
  ProductImage,
  ProductName,
  ModalContent,
  SubmitContainer,
  Label,
  QuantityUpdateContainer,
  LocationAutocomplete,
  Modal,
  QuantityCounter,
  UpdateButton,
  CloseButton,
  CloseIcon,
  ProductDetails,
  Form,
  QuantityLabel,
  PriceWidget,
  HelperText,
  Seperator,
  FormItem,
  SelectLocation,
  LocationIcon,
  SearchIcon,
  DateRangePicker,
  ContentWrapper,
  ContactUs,
  ContactUsNumber,
  ContactUsWrapper,
  BottomSheetTopBar,
  BottomSheetLabel,
  BottomsheetContentWrapper,
  DateNotificationContainer,
  DateNotificationIcon,
  DateNotificationText,
} from './CartModalStyle';

dayjs.extend(utc);

const focusInput = new Subject<boolean>();

type CartModalErrorState = {
  locationError?: boolean;
  startDateError?: boolean;
  endDateError?: boolean;
};

enum CartModalVisibility {
  NONE = 'NONE',
  CENTERED_MODAL = 'CENTERED_MODAL',
  BOTTOM_SHEET = 'BOTTOM_SHEET',
}

export enum CartModalType {
  CENTERED_MODAL = 'CENTERED_MODAL',
  BOTTOM_SHEET = 'BOTTOM_SHEET',
}

interface AddToCartInterface {
  cartModalConfig: CartModalConfigType;
  cartModalType: CartModalType;
  handleClose: () => void;
}

function AddToCartForm({ cartModalConfig, cartModalType, handleClose }: AddToCartInterface) {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const location = useAppSelector((state: RootState) => state.location.userLocation);
  const branch = useAppSelector((state: RootState) => state.location.branch);
  const auth = useAppSelector((state: RootState) => state.auth);
  const siteLevelDate = useAppSelector((state: RootState) => state.cart.siteLevelDate);
  const cartTimezone = useAppSelector((state: RootState) => state.cart.timezone);

  const [showContactUsMessage, setShowContactUsMessage] = useState<boolean>(false);
  const priceEntry = useAppSelector(
    (state: RootState) =>
      state.prices.priceCatalog[getPriceCatalogKey(cartModalConfig.equipmentId)],
  );

  const [currentQuantity, setCurrentQuantity] = useState<number>();
  const [currentDeliveryType, setCurrentDeliveryType] = useState<DeliveryType | undefined>(
    DeliveryType.PICKUP,
  );
  const [startDate, setStartDate] = useState<Dayjs | null | unknown>(null);
  const [endDate, setEndDate] = useState<Dayjs | null | unknown>(null);
  const [errorState, setErrorState] = useState<CartModalErrorState | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);

  const locationHelperTexts: LocationHelperTexts = useMemo(
    () => ({
      default: {
        element: (
          <HelperText hasError={errorState?.locationError}>
            {errorState?.locationError
              ? formatMessage({ id: 'cart.cartModal.locationRequired' })
              : !location
              ? formatMessage({ id: 'generic.locationHelperTexts.default' })
              : ''}
          </HelperText>
        ),
      },
      invalidInput: {
        element: (
          <HelperText hasError absolutePosition>
            <FormattedMessage id='generic.locationHelperTexts.invalidLocation' />
          </HelperText>
        ),
        position: 'absolute',
      },
    }),
    [errorState, location],
  );

  const initDates = () => {
    if (cartModalConfig.startDate && cartModalConfig.endDate) {
      const parsedStartDate = dayjs.unix(cartModalConfig.startDate);
      const parsedEndDate = dayjs.unix(cartModalConfig.endDate);

      if (dayjs(parsedStartDate).isValid() && dayjs(parsedEndDate).isValid()) {
        setStartDate(parsedStartDate);
        setEndDate(parsedEndDate);
        return;
      }
    }

    if (siteLevelDate?.start && siteLevelDate?.end) {
      const parsedSiteLevelStartDate = dayjs.unix(siteLevelDate?.start);
      const parsedSiteLevelEndDate = dayjs.unix(siteLevelDate?.end) as Dayjs;
      if (dayjs(parsedSiteLevelStartDate).isValid() && dayjs(parsedSiteLevelEndDate).isValid()) {
        setStartDate(parsedSiteLevelStartDate);
        setEndDate(parsedSiteLevelEndDate);
        return;
      }
    }

    setStartDate(null);
    setEndDate(null);
  };

  const fallbackImageManager = useFallbackImageManager();

  const onImageError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    fallbackImageManager.applyFallbackImage(e, NoIconImage);
  };

  useEffect(() => {
    if (!cartModalConfig) {
      return;
    }
    setCurrentQuantity(cartModalConfig.quantity ?? 1);
    setCurrentDeliveryType(cartModalConfig.deliveryType);
    initDates();
  }, [cartModalConfig]);

  const minStartDate = dayjs.utc().local().add(1, 'day');

  const updateCartButtonHandler = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    try {
      event.stopPropagation();

      if (!cartModalConfig || !validateInputs()) {
        return;
      }

      setIsAdding(true);

      const startDateTimestamp = dayjs(String(startDate)).unix();
      const endDateTimestamp = dayjs(String(endDate)).unix();

      if (
        !Number.isNaN(startDateTimestamp) &&
        !Number.isNaN(endDateTimestamp) &&
        typeof currentQuantity === 'number' &&
        startDate &&
        endDate &&
        currentDeliveryType !== DeliveryType.UNASSIGNED &&
        location !== null &&
        branch !== null &&
        !checkInvalidStartDate(startDateTimestamp, cartTimezone)
      ) {
        const equipmentClassSearchResults = await getEquipmentClassListSearchResults(
          { page: 1, perPage: 1 },
          { equipmentClassIds: [Number(cartModalConfig.equipmentId)] },
        );
        const equipment = equipmentClassSearchResults?.equipment_classes[0];

        const cartItem: CartItem = {
          equipmentId: cartModalConfig.equipmentId,
          categoryName: equipment?.category_name,
          categoryId: equipment?.category_id,
          categoryIdHierarchy: equipment?.category_id_hierarchy,
          categoryNameHierarchy: equipment?.category_name_hierarchy,
          name: cartModalConfig.equipmentClassName,
          imageSource: cartModalConfig.imageSource ?? NoIconImage,
          slug: cartModalConfig.slug,
          quantity: currentQuantity,
          startDate: startDateTimestamp,
          endDate: endDateTimestamp,
          isStartDateValid: !checkInvalidStartDate(startDateTimestamp, cartTimezone),
        };

        if (cartModalConfig.cartOperationType === CartOperationType.ADD) {
          const priceCatalogKey = getPriceCatalogKey(cartModalConfig.equipmentId);
          const priceEntry = store.getState().prices.priceCatalog[priceCatalogKey];
          if (!priceEntry || (priceEntry && !isPriceInitialized(priceEntry.status))) {
            return;
          }
          if (!priceEntry?.price) {
            openMessageModal(messageContents.missingEquipmentPrice);
            return;
          }
          const payload = {
            index: cartModalConfig.index,
            actionType: CartOperationType.ADD,
            cartUpdateObject: cartItem,
          };
          dispatch(setCartItem(payload));

          pushEventToDataLayer({
            type: DataLayerEventName.PRODUCT_ADDED,
            payload: { cartItem: cartItem },
          });
        } else if (cartModalConfig.cartOperationType === CartOperationType.UPDATE) {
          const payload = {
            index: cartModalConfig.index,
            actionType: CartOperationType.UPDATE,
            cartUpdateObject: cartItem,
          };
          dispatch(setCartItem(payload));
        }
        handleClose();

        if (cartModalConfig.toggleCartHeaderOnUpdate) {
          if (activeMenu.getValue() !== Menu.CART_MENU) {
            dispatchToggleMenuEvent(Menu.CART_MENU);
          }
          dispatch(toggleCartHeaderDropdown({ open: true, index: cartModalConfig.index }));
        }
      } else {
        if (currentDeliveryType === DeliveryType.UNASSIGNED)
          logger('error', `Delivery type is not picked`);
        if (Number.isNaN(startDateTimestamp) || !startDate) {
          logger('error', `Start date is not picked`);
        }
        if (Number.isNaN(endDateTimestamp) || !endDate) {
          logger('error', `End date is not picked`);
        }
        if (location === null || branch === null) {
          logger('error', 'Jobsite location is not selected');
        }
      }
    } catch (err) {
      console.error(`Error at updateCartButtonHandler: ${err}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleClick = () => {
    focusInput.next(true);
  };

  const validateInputs = (): boolean => {
    let isValid: boolean = false;
    if (!!location && !!startDate && !!endDate) {
      isValid = true;
    }
    setErrorState(() => ({
      locationError: location ? false : true,
      startDateError: startDate ? false : true,
      endDateError: endDate ? false : true,
    }));
    return isValid;
  };

  useEffect(() => {
    setErrorState((prev) => ({
      locationError: location ? false : prev?.locationError,
      startDateError: startDate ? false : prev?.startDateError,
      endDateError: endDate ? false : prev?.endDateError,
    }));
  }, [location, startDate, endDate]);

  useEffect(() => {
    if (priceEntry && isPriceInitialized(priceEntry.status)) {
      setShowContactUsMessage(showContactForPrice(priceEntry.price));
    }
  }, [priceEntry]);

  useEffect(() => {
    // Resets the current location status on the closing of the component
    return function reset() {
      currentLocationStatus.next(CurrentLocationStatus.NONE);
    };
  }, []);

  if (!cartModalConfig) {
    return <></>;
  }

  return (
    <>
      <ConfigContainer cartModalType={cartModalType}>
        <Product>
          <ProductImage
            src={cartModalConfig.imageSource ?? NoIconImage}
            alt={`image for ${cartModalConfig.equipmentClassName}`}
            onError={onImageError}
          />
          <ProductDetails>
            <ProductName cartModalType={cartModalType}>
              {cartModalConfig.equipmentClassName ?? ''}
            </ProductName>
            {location?.address ? (
              <PriceWidget
                equipmentId={cartModalConfig.equipmentId}
                day
                week
                month
                cartModalType={cartModalType}
                showPriceLabel={isAuthenticated(auth.loginStatus)}
              />
            ) : (
              <SelectLocation onClick={handleClick}>
                <LocationIcon />
                Enter jobsite location for accurate pricing
              </SelectLocation>
            )}
          </ProductDetails>
        </Product>
        {showContactUsMessage && (
          <ContactUsWrapper>
            <ContactUs>
              For pricing, please contact us directly at{' '}
              <ContactUsNumber
                data-event-tracking={AnalyticsEventAttribute.MainPhone}
                href='tel:+18888073687'
              >
                1.888.80.RENTS
              </ContactUsNumber>
            </ContactUs>
          </ContactUsWrapper>
        )}

        <Seperator />

        <Form>
          <FormItem>
            <Label>Where is your jobsite?</Label>
            <LocationAutocomplete
              resetCurrentLocationOnClickAway={true}
              hideStartIcon={true}
              inputFocusSubject={focusInput}
              endIcon={<SearchIcon />}
              helperText={locationHelperTexts}
              hasError={errorState?.locationError}
              showCurrentLocationSelector
            />
          </FormItem>
          <FormItem>
            <DateRangePicker
              minStartDate={minStartDate}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              startDateLabel={<Label>Start date</Label>}
              endDateLabel={<Label>End date</Label>}
              startDateHelperLabel={
                errorState?.startDateError ? (
                  <HelperText hasError>Start date required</HelperText>
                ) : (
                  <></>
                )
              }
              endDateHelperLabel={
                errorState?.endDateError ? (
                  <HelperText hasError>End date required</HelperText>
                ) : (
                  <></>
                )
              }
              startDateError={errorState?.startDateError}
              endDateError={errorState?.endDateError}
            ></DateRangePicker>
            {!!startDate &&
              checkInvalidStartDate(dayjs(String(startDate)).unix(), cartTimezone) && (
                <HelperText hasError>
                  <FormattedMessage id='cart.cartModal.selectFutureDates' />
                </HelperText>
              )}
            {siteLevelDate && (
              <DateNotificationContainer>
                <DateNotificationIcon />
                <DateNotificationText>
                  <FormattedMessage id='cart.cartModal.dateNotification' />
                </DateNotificationText>
              </DateNotificationContainer>
            )}
          </FormItem>
        </Form>
      </ConfigContainer>

      <SubmitContainer cartModalType={cartModalType}>
        <QuantityLabel>Quantity</QuantityLabel>
        <QuantityUpdateContainer>
          <QuantityCounter
            quantity={currentQuantity ?? ''}
            setQuantity={setCurrentQuantity}
          ></QuantityCounter>
          <UpdateButton
            data-event-tracking={AnalyticsEventAttribute.AddToCart}
            onClick={(e) => updateCartButtonHandler(e)}
            disabled={isAdding}
          >
            {cartModalConfig.cartOperationType == CartOperationType.ADD
              ? 'ADD TO CART'
              : 'UPDATE CART'}
          </UpdateButton>
        </QuantityUpdateContainer>
      </SubmitContainer>
    </>
  );
}

export default function CartModal() {
  const [isOpen, setIsOpen] = useState<CartModalVisibility>(CartModalVisibility.NONE);
  const cartModalConfig = useAppSelector((state: RootState) => state.cart.cartModalConfig);
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(closeCartModal());
  };

  useEffect(() => {
    if (!cartModalConfig || !cartModalConfig.open) {
      setIsOpen(CartModalVisibility.NONE);
    } else {
      if (window.innerWidth <= 786) {
        setIsOpen(CartModalVisibility.BOTTOM_SHEET);
      } else {
        setIsOpen(CartModalVisibility.CENTERED_MODAL);
      }
    }
  }, [cartModalConfig]);

  if (!cartModalConfig) {
    return <></>;
  }

  return (
    <>
      <Modal
        open={isOpen === CartModalVisibility.CENTERED_MODAL}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <ModalContent className='cart-modal-content'>
          <ContentWrapper>
            <CloseButton onClick={handleClose} cartModalType={CartModalType.CENTERED_MODAL}>
              <CloseIcon></CloseIcon>
            </CloseButton>
            <AddToCartForm
              cartModalConfig={cartModalConfig}
              handleClose={handleClose}
              cartModalType={CartModalType.CENTERED_MODAL}
            />
          </ContentWrapper>
        </ModalContent>
      </Modal>
      <BottomSheet isOpen={isOpen === CartModalVisibility.BOTTOM_SHEET} onClose={handleClose}>
        <BottomsheetContentWrapper>
          <BottomSheetTopBar>
            <BottomSheetLabel>Add to cart</BottomSheetLabel>
            <CloseButton onClick={handleClose} cartModalType={CartModalType.BOTTOM_SHEET}>
              <CloseIcon></CloseIcon>
            </CloseButton>
          </BottomSheetTopBar>
          <AddToCartForm
            cartModalConfig={cartModalConfig}
            handleClose={handleClose}
            cartModalType={CartModalType.BOTTOM_SHEET}
          />
        </BottomsheetContentWrapper>
      </BottomSheet>
    </>
  );
}
